import React, { useState, useEffect } from 'react';
import { Button, Form, Spin, Modal, Result, Alert, Input } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import buttons from '@components/ui-kit/buttons/submitButton.module.css';
import styles from './generate-image.module.css';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { makeId } from 'util/helpers';

type FormValues = {
  prompt: string;
};
type MessageType = string | null | undefined;
type AlertType = 'success' | 'error' | 'info' | undefined;
const FILE_TYPE = 'assets';

const URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
const SD_TXT2IMG_URL = process.env.NEXT_PUBLIC_SD_SERVICE_TXT2IMG_URL;

const uploadImage = async (file: File, creator: string, type: string) => {
  const data = new FormData();
  data.set('file', file);
  data.set('fileName', file.name);
  data.set('email', creator);
  data.set('fileType', type);

  const res = await fetch('/api/upload-file-s3', {
    method: 'POST',
    body: data,
  }).then((res) => res.json());

  if (!res?.status) throw new Error('Error when uploading image');
};

export function GenerateImage({
  userEmail,
  onCloseModal,
  callback,
  loading,
  setLoading,
}) {
  const [message, setMessage] = useState(null as MessageType);
  const [type, setType] = React.useState('error' as AlertType);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!URL || !SD_TXT2IMG_URL) {
      throw new Error(
        'Configuration error occurred, please contact Ozone tech support.'
      );
    }
  }, []);

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setType('info');
    setMessage('Generating your image please wait...');

    let prompt = values.prompt;

    if (!prompt) {
      setMessage('Error setting prompt. Please try again');
      setLoading(false);
      return;
    }

    try {
      const url = SD_TXT2IMG_URL;
      const res = await fetch(url as string, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
        }),
      }).then((res) => res.blob());

      const generatedImage = new File([res], `generated-img-${makeId(10)}.png`);

      await uploadImage(generatedImage, userEmail, FILE_TYPE);

      const assetURL = `${URL}/${encodeURIComponent(
        userEmail
      )}/${FILE_TYPE}/${encodeURIComponent(generatedImage.name)}`;

      callback({
        name: generatedImage.name.replace(/\.[^/.]+$/, ''),
        url: assetURL,
        type: SIDE_MENU_EVENTS.IMAGE_PLANE,
      });
      setIsModalOpen(true);
    } catch (error: any) {
      setType('error');
      if (error.toString() === 'TypeError: Failed to fetch') {
        setMessage('Experimental feature: AI server is currently disabled.');
      } else {
        setMessage(
          "Something went wrong during the asset upload. Let's try again."
        );
      }
    }

    setLoading(false);
  };

  const onClose = () => {
    onCloseModal();
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>Generate Image</h1>
        <div className={styles.formContainer}>
          <Form className={styles.form} onFinish={onSubmit} layout='vertical'>
            <Form.Item
              label='Write what you want to generate:'
              name='prompt'
              className={styles.formItem}
            >
              <Input className={styles.input} />
            </Form.Item>
            {message && (
              <Alert
                type={type}
                message={message}
                showIcon
                style={{
                  marginBottom: 25,
                }}
              />
            )}
            <Button
              htmlType='submit'
              type='primary'
              disabled={loading}
              className={buttons.primaryButton}
              style={{
                width: 140,
                alignSelf: 'flex-end',
              }}
            >
              {loading ? (
                <Spin
                  style={{ width: '100%' }}
                  indicator={<LoadingOutlined style={{ fontSize: 35 }} spin />}
                ></Spin>
              ) : (
                <span style={{ color: '#fff' }}>Generate</span>
              )}
            </Button>
          </Form>
          <Modal
            open={isModalOpen}
            title={false}
            footer={false}
            onCancel={onClose}
            zIndex={1010}
          >
            <Result
              status='success'
              title='Successfully uploaded Asset!'
              extra={[
                <Button
                  type='primary'
                  key='ok'
                  className={buttons.primaryButton}
                  onClick={onClose}
                >
                  <span style={{ color: '#fff' }}>Close</span>
                </Button>,
              ]}
            />
          </Modal>
        </div>
      </div>
    </div>
  );
}
